import React, { useState, useRef, useEffect } from "react";
import { Typography, Tooltip, Button } from "src/assets/imports/antdComponents";
import { IReferenceItem, IReflectiveReferenceItemProps, StudyType, TIMING } from "../../models/interfaces";
import { useFeatures } from "src/config/featureManager";
import { useTranslation } from "react-i18next";
import { FeatureKeysType } from "src/pages/Studies/models/interfaces";
import useIndexedDB from "src/components/useIndexDB";
import "./style.css";

const { Paragraph } = Typography;

const ReflectiveReferenceItem: React.FC<IReflectiveReferenceItemProps> = ({
	item,
	onTranscriptClick,
	activeCitationNo,
	activeTab,
	handleFileDownload,
	isAudioVideoEnabled = false,
}) => {
	const { t } = useTranslation();
	const paragraphRef = useRef<HTMLDivElement>(null);
	const citationRef = useRef<HTMLDivElement>(null);

	const { context_preview: contextPreview, file_name, entity_name, citation_id, context_preview_image } = item;
	const isHighlighted = activeCitationNo === citation_id;
	const [expanded, setExpanded] = useState(false);
	const [copied, setCopied] = useState(false);
	const { isFeatureAvailable } = useFeatures();

	const isMultiLingualSupportEnabled = isFeatureAvailable(FeatureKeysType.MultiLingualSupport);

	// A/V preview image — only loaded when is_audio_video + image_path present.
	// Reuses the same IndexedDB cache key as TranscriptPreviewPopover, so a prior hover
	// hydrates this instantly and no duplicate fetch happens.
	const ALLOWED_IMAGE_CONTENT_TYPES = useRef(new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"])).current;
	const avImagePath = context_preview_image?.image_path;
	const hasAvImage = !!(isAudioVideoEnabled && item?.is_audio_video && avImagePath);

	const [avImageUrl, setAvImageUrl] = useState<string | null>(null);
	const [isAvImageLoading, setIsAvImageLoading] = useState(false);
	const [avImageError, setAvImageError] = useState<string | null>(null);
	const prevAvBlobUrlRef = useRef<string | null>(null);
	const avAbortRef = useRef<AbortController | null>(null);
	const { addOrUpdate: avAddOrUpdate, getItem: avGetItem } = useIndexedDB("reportCacheDB", "files");
	const avDbRef = useRef({ addOrUpdate: avAddOrUpdate, getItem: avGetItem });
	useEffect(() => {
		avDbRef.current = { addOrUpdate: avAddOrUpdate, getItem: avGetItem };
	}, [avAddOrUpdate, avGetItem]);

	useEffect(() => {
		if (!hasAvImage || !avImagePath) {
			setAvImageUrl(null);
			setIsAvImageLoading(false);
			setAvImageError(null);
			return;
		}

		avAbortRef.current?.abort();
		const controller = new AbortController();
		avAbortRef.current = controller;
		let cancelled = false;

		const cacheKey = `preview-av-${item?.tr_id}-${item?.first_dialog_no ?? "0"}`;

		const loadImage = async () => {
			setIsAvImageLoading(true);
			setAvImageError(null);

			try {
				const { getItem: dbGet, addOrUpdate: dbPut } = avDbRef.current;
				let blobUrl: string;

				let cachedBlob: Blob | null = null;
				try {
					const cachedFile = await dbGet(cacheKey);
					if (cachedFile?.fileBlob && ALLOWED_IMAGE_CONTENT_TYPES.has(cachedFile.fileBlob.type)) {
						cachedBlob = cachedFile.fileBlob;
					}
				} catch {
					// cache miss — fall through
				}

				if (cancelled || controller.signal.aborted) return;

				if (cachedBlob) {
					blobUrl = window.URL.createObjectURL(cachedBlob);
				} else {
					const response = await fetch(avImagePath, {
						method: "GET",
						mode: "cors",
						cache: "no-cache",
						credentials: "omit",
						signal: controller.signal,
					});
					if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

					const contentType = response.headers.get("Content-Type")?.split(";")[0]?.trim().toLowerCase();
					if (!contentType || !ALLOWED_IMAGE_CONTENT_TYPES.has(contentType)) {
						throw new Error(`Invalid content type: ${contentType}`);
					}

					const blob = await response.blob();
					if (cancelled || controller.signal.aborted) return;

					try {
						await dbPut({ id: cacheKey, fileBlob: blob });
					} catch (cacheError) {
						console.error("Failed to cache A/V image:", cacheError);
					}

					blobUrl = window.URL.createObjectURL(blob);
				}

				if (cancelled || controller.signal.aborted) {
					window.URL.revokeObjectURL(blobUrl);
					return;
				}

				if (prevAvBlobUrlRef.current) {
					window.URL.revokeObjectURL(prevAvBlobUrlRef.current);
				}
				prevAvBlobUrlRef.current = blobUrl;
				setAvImageUrl(blobUrl);
			} catch (err: any) {
				if (err?.name === "AbortError" || cancelled) return;
				console.error("Failed to load A/V preview image:", err);
				setAvImageError("Failed to load image");
			} finally {
				if (!cancelled) setIsAvImageLoading(false);
			}
		};

		loadImage();

		return () => {
			cancelled = true;
			controller.abort();
		};
	}, [hasAvImage, avImagePath, item?.tr_id, item?.first_dialog_no, ALLOWED_IMAGE_CONTENT_TYPES]);

	// Final cleanup on unmount
	useEffect(() => {
		return () => {
			if (prevAvBlobUrlRef.current) window.URL.revokeObjectURL(prevAvBlobUrlRef.current);
			avAbortRef.current?.abort();
		};
	}, []);

	useEffect(() => {
		if (activeCitationNo === citation_id && citationRef.current) {
			setTimeout(() => {
				citationRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			}, 10);
		}
	}, [activeCitationNo, citation_id]);

	const handleCopy = () => {
		const textToCopy = `[${citation_id}] ${contextPreview?.text || contextPreview}\n\nStudy: ${entity_name}\nFile: ${file_name}\n`;

		navigator.clipboard
			.writeText(textToCopy)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), TIMING?.COPY_FEEDBACK_DURATION);
			})
			.catch((err) => {
				console.error("Failed to copy text: ", err);
			});
	};

	const parseStringPreview = (text: string): React.ReactNode => {
		return (
			<p className={`font-normal ${isHighlighted ? "highlight-container" : ""}`}>
				<span className={`dialogue-item truncate-text-4-lines ${isHighlighted ? "highlight-block" : ""}`}>
					{citation_id ? <strong className="secondary-color">[{citation_id}] </strong> : null}
					{text}
				</span>
			</p>
		);
	};

	const toggleExpanded = () => {
		setExpanded(!expanded);
	};

	return (
		<div className="reference-item" id={`reference-item-${citation_id}`} ref={citationRef}>
			{isMultiLingualSupportEnabled && item?.study_language && (
				<div className="flex items-center mb-3">
					<i className="flex items-start material-symbols-outlined text-[1rem] text-[--neutral-grey] mr-1">translate</i>
					<span className="reference-item-languague">{item.study_language.name}</span>
				</div>
			)}

			{activeTab === StudyType.Reports ? (
				<>
					<div className={`transcript-title-wrapper mb-2 ${isHighlighted ? "highlighted" : ""}`}>
						<div className="flex w-full justify-between">
							<Tooltip title={item?.is_study_access_available ? "" : t(item?.display_message)} placement="top">
								<div
									className={`report-name text-base-1 ${item?.is_study_access_available ? "" : "reference-item-not-available"}`}
									onClick={() => onTranscriptClick(item)}>
									{`${entity_name}/${file_name}`}
								</div>
							</Tooltip>
							<Tooltip title={<div className="tooltip-tag-container">{contextPreview?.text || contextPreview}</div>} placement="left">
								<i className="material-symbols-outlined secondary-color text-2xl ml-auto mr-1">info</i>
							</Tooltip>
							{handleFileDownload && (
								<Tooltip title={item?.is_study_access_available ? "" : t(item?.display_message)} placement="topRight">
									<Button
										disabled={!item?.is_study_access_available}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleFileDownload(item);
										}}
										className="m-0 p-0 h-8"
										type="text"
										icon={<i className="material-symbols-outlined text-2xl ml-auto mr-1">download</i>}
									/>
								</Tooltip>
							)}
						</div>
					</div>
					{context_preview_image?.image_path && (
						<img src={String(context_preview_image.image_path)} alt={`${file_name}`} className="thumbnail text-base-1" onClick={() => onTranscriptClick(item)} />
					)}
				</>
			) : (
				<>
					<div className="flex justify-between items-start space-x-1">
						<div className="flex flex-col w-full">
							<Paragraph
								className={`ref-summary-para text-base-1 secondary-color cursor-pointer ${expanded ? "" : "preview-mode"}`}
								onClick={toggleExpanded}
								ref={paragraphRef}>
								{parseStringPreview(typeof contextPreview === "string" ? contextPreview : contextPreview?.text)}
							</Paragraph>
						</div>
						<div className="copy-icon-reference">
							<Tooltip placement="left" title={copied ? t("Copied to clipboard") : t("Copy")}>
								<i onClick={handleCopy} className="material-symbols-outlined text-[--primary-color] text-2xl ask-kural-icon cursor-pointer">
									{copied ? "check" : "copy_all"}
								</i>
							</Tooltip>
						</div>
					</div>

					{hasAvImage && (
						<div
							className="av-ref-image-wrapper"
							onClick={() => item?.is_study_access_available && onTranscriptClick(item)}
							role={item?.is_study_access_available ? "button" : undefined}
							tabIndex={item?.is_study_access_available ? 0 : -1}
							onKeyDown={(e) => {
								if ((e.key === "Enter" || e.key === " ") && item?.is_study_access_available) {
									e.preventDefault();
									onTranscriptClick(item);
								}
							}}
							aria-label={t("Preview image — open transcript")}>
							{isAvImageLoading && <div className="av-ref-image-state">{t("Loading image...")}</div>}
							{avImageError && !isAvImageLoading && <div className="av-ref-image-state av-ref-image-state-error">{avImageError}</div>}
							{!isAvImageLoading && !avImageError && avImageUrl && (
								<>
									<img src={avImageUrl} alt={item?.file_name || t("A/V preview")} className="av-ref-image" />
									<div className="av-ref-play-overlay" aria-hidden="true">
										<i className="material-symbols-outlined av-ref-play-icon">play_circle</i>
									</div>
								</>
							)}
						</div>
					)}

					<div className="transcript-title-wrapper space-x-1">
						<Tooltip title={item?.is_study_access_available ? "" : t(item?.display_message)} placement="top">
							<div
								className={`transcript-name text-base text-[--secondary-color-2] !font-semibold ${
									item?.is_study_access_available ? "" : "reference-item-not-available"
								}`}
								onClick={() => onTranscriptClick(item)}>
								{`${entity_name}/${file_name}`}
							</div>
						</Tooltip>

						<i className="material-symbols-outlined text-2xl leading-none cursor-pointer text-[--secondary-color]" onClick={() => onTranscriptClick(item)}>
							keyboard_arrow_right
						</i>
					</div>
				</>
			)}
		</div>
	);
};

export default ReflectiveReferenceItem;
